import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, TrendingUp, MapPin } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";

export default function SEOMiamiPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "SEO en Miami",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": {
      "@type": "City",
      "name": "Miami",
      "addressRegion": "FL"
    },
    "description": "Servicios profesionales de SEO Local en Miami, Florida. Posicionamiento orgánico bilingüe para negocios hispanos con estrategias medibles."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "¿Por qué el SEO en Miami requiere estrategia bilingüe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "En Miami una parte muy relevante de la demanda busca en español y otra en inglés, con vocabulario distinto. Una estrategia bilingüe real (no traducción literal) permite cubrir ambos segmentos."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cuánto cuesta el SEO en Miami?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "El alcance y la inversión se definen según la competencia del mercado y los objetivos del proyecto. Incluye SEO técnico, contenido, señales locales y reporting."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Servicios", "item": "https://luisroc.com/services" },
      { "@type": "ListItem", "position": 2, "name": "Florida", "item": "https://luisroc.com/servicios-locales-florida" },
      { "@type": "ListItem", "position": 3, "name": "SEO Miami", "item": "https://luisroc.com/seo-miami" }
    ]
  };

  return (
    <>
      <Helmet>
        <html lang="es" />
        <title>Agencia SEO en Miami, FL | Posicionamiento Bilingüe – Luis ROC</title>
        <meta name="description" content="SEO Local en Miami, Florida. Estrategia bilingüe para negocios hispanos. Mejora tu visibilidad en Google en español e inglés con resultados medibles." />
        <link rel="canonical" href="https://luisroc.com/seo-miami" />
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Miami, Florida" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://luisroc.com/seo-miami" />
      </Helmet>

      <Header language="es" onLanguageChange={() => {}} />
      
      <main className="min-h-screen bg-background">
        <BreadcrumbNav 
          items={[
            { name: "Servicios", url: "/servicios" },
            { name: "Florida", url: "/servicios-locales-florida" },
            { name: "SEO Miami", url: "/seo-miami" }
          ]}
          language="es"
        />

        <section className="bg-gradient-to-br from-primary/10 via-background to-background py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <MapPin className="w-6 h-6 text-primary" />
                <span className="text-lg font-semibold text-primary">Miami, Florida</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Agencia SEO en Miami - Estrategia Bilingüe para el Mercado Latino
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Mejora tu visibilidad en Google en español e inglés. SEO local optimizado para el mercado hispano más importante de EE.UU.
              </p>
              <Button asChild size="lg">
                <a href="/contacto">
                  Agenda tu reunión inicial gratuita de 30 minutos
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
                SEO Local para Miami y Área Metropolitana
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Estrategia Bilingüe</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Contenido optimizado en español e inglés para capturar todo el mercado de Miami: Downtown, Brickell, Coral Gables, Hialeah.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Google Business Profile</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Optimización completa para aparecer en búsquedas locales, Google Maps y capturar tráfico de "near me".
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Link Building Latino</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Enlaces desde medios hispanos de Miami, directorios locales y partnerships estratégicos.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <TrendingUp className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4 text-foreground">
                ¿Listo para Dominar las Búsquedas en Miami?
              </h2>
              <Button asChild size="lg">
                <a href="/contacto">
                  Agenda tu Consultoría
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

        <KnowledgeConnections path="/seo-miami" language="es" />

      <Footer language="es" />
      <JsonLd data={serviceSchema} />
      <JsonLd data={faqSchema} />
      <JsonLd data={breadcrumbSchema} />
    </>
  );
}
